import _ from 'lodash'
import {removeInvisibleCharacters} from '../../utils/string-helpers'

// Pure, render-derivable gating logic for the photo submission wizard.
//
// The wizard previously stored `nextIsDisabled` in React state and updated it
// from a useEffect. Because effects run after paint, the value lagged by one
// render whenever `currentStep` changed, so a rapid second click (e.g. a
// double-click on "Next") could act on the *previous* step's gate and advance
// past — or submit at — a page before its validation engaged. Deriving the gate
// during render and re-checking it inside the advance handler removes that race.

export interface WizardGateFormField {
  required?: boolean
  value: string | boolean | number
}

export interface WizardGateInput {
  stepTitle?: string
  submissionName?: string
  submissionUrl?: string
  additionalFormFields?: WizardGateFormField[]
  uploadcareFilesCount?: number
}

export interface WizardPreviousInput {
  stepTitle?: string
  paymentIsProcessing?: boolean
  submissionIsProcessing?: boolean
}

// Treat whitespace-only and invisible/zero-width-only strings as empty so a
// required field can't be satisfied by spaces or pasted invisible characters.
// Non-string values (required checkbox booleans, numeric select values) keep a
// plain truthiness check.
const isFieldFilled = (value: string | boolean | number): boolean =>
  typeof value === 'string' ? _.trim(removeInvisibleCharacters(value)).length > 0 : Boolean(value)

const isNonEmptyText = (value: string | undefined): boolean =>
  _.trim(removeInvisibleCharacters(value ?? '')).length > 0

/**
 * Whether the "Next"/"Finish" action should be disabled for the given step.
 * Pure: depends only on its inputs, so it is safe to call during render and
 * again inside the advance handler.
 */
export const isNextDisabledForStep = (input: WizardGateInput): boolean => {
  switch (input.stepTitle) {
  case 'Upload images':
    return (input.uploadcareFilesCount ?? 0) < 1
  case 'Add metadata': {
    const requiredValues = _.chain(input.additionalFormFields)
      .filter('required')
      .map('value')
      .value() as Array<string | boolean | number>
    const requiredFields = [...requiredValues, input.submissionName ?? '']
    return !_.every(requiredFields, isFieldFilled)
  }
  case 'Pay with Stripe':
    return !(isNonEmptyText(input.submissionName) && isNonEmptyText(input.submissionUrl))
  case 'Demonstrate success':
    return false
  default:
    return false
  }
}

/** Whether the "Previous" action should be disabled for the given step. */
export const isPreviousDisabledForStep = (input: WizardPreviousInput): boolean => {
  switch (input.stepTitle) {
  case 'Pay with Stripe':
    return Boolean(input.paymentIsProcessing) || Boolean(input.submissionIsProcessing)
  case 'Upload images':
  case 'Add metadata':
  case 'Demonstrate success':
    return false
  default:
    return true
  }
}

/**
 * Returns the index to advance to from `currentStep`, re-checking the current
 * step's gate. If the gate is closed the index is unchanged, so a stale or
 * rapid second advance can never skip a still-blocked step. Designed to be used
 * inside a functional state updater where `currentStep` is the authoritative
 * previous value.
 */
export const getGatedNextStepIndex = (
  stepTitles: string[],
  currentStep: number,
  gate: WizardGateInput,
): number => {
  const stepTitle = stepTitles[currentStep]
  if (isNextDisabledForStep({...gate, stepTitle})) return currentStep
  return Math.min(currentStep + 1, stepTitles.length - 1)
}
